import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LandingPage from "./pages/LandingPage";
import AgentPricingPage from "./pages/AgentPricingPage";
import AgentReferralForm from "./pages/AgentReferralForm";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import QuestionnaireStep from "./pages/steps/QuestionnaireStep";
import DocumentsStep from "./pages/steps/DocumentsStep";
import DS160Step from "./pages/steps/DS160Step";
import OptimizationStep from "./pages/steps/OptimizationStep";
import ResultStep from "./pages/steps/ResultStep";
import HelpCenter from "./pages/HelpCenter";
import Notifications from "./pages/Notifications";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCommandCenter from "./pages/admin/AdminCommandCenter";
import AdminCases from "./pages/admin/AdminCases";
import AdminCaseDetail from "./pages/admin/AdminCaseDetail";
import AdminChat from "./pages/admin/AdminChat";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminRealTimeMetrics from "./pages/admin/AdminRealTimeMetrics";
import AdminExcludedIPs from "./pages/admin/AdminExcludedIPs";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminCreateClient from "./pages/admin/AdminCreateClient";
import AdminDocuments from "./pages/admin/AdminDocuments";
import AdminClientSearch from "./pages/admin/AdminClientSearch";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminCaseManagement from "./pages/admin/AdminCaseManagement";
import AdminStepsDashboard from "./pages/admin/AdminStepsDashboard";
import AdminJobApplications from "./pages/admin/AdminJobApplications";
import SLADashboard from "./pages/admin/SLADashboard";
import InterviewQuestions from "./pages/InterviewQuestions";
import InterviewSimulator from "./pages/premium/InterviewSimulator";
import ChatPage from "./pages/ChatPage";
import DS160Assistant from "./pages/DS160Assistant";
import DS160MockupDemo from "./pages/DS160MockupDemo";
import DS160Sections from "./pages/DS160Sections";
import SimulacionesPanel from "./pages/admin/features/SimulacionesPanel";
import DS160ReviewPage from "./pages/admin/DS160ReviewPage";
import AdminDS160Pendientes from "./pages/admin/AdminDS160Pendientes";
import DocumentHistory from "./pages/DocumentHistory";
import PaymentLinkPage from "./pages/PaymentLinkPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ScheduleSimulation from "./pages/ScheduleSimulation";
import AdminMarketingPanel from "./pages/Admin/AdminMarketingPanel";
import AdminCommissions from "./pages/admin/AdminCommissions";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminReminders from "./pages/admin/AdminReminders";
import AdminLiveVisitors from "./pages/admin/AdminLiveVisitors";
import AdminPaymentMetrics from "./pages/admin/AdminPaymentMetrics";
import AdminPaymentsPending from "./pages/admin/AdminPaymentsPending";
import AdminReferralCommissions from "./pages/admin/AdminReferralCommissions";
import SiteVisitorTracker from "./components/analytics/SiteVisitorTracker";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import SupportChatPage from "./pages/SupportChatPage";
import AdminSupportChat from "./pages/admin/AdminSupportChat";
import AISimulationPage from "./pages/AISimulationPage";
import AutoRevisionPage from "./pages/AutoRevisionPage";
import UpgradePlanPage from "./pages/UpgradePlanPage";
import AdminStandardQueue from "./pages/admin/AdminStandardQueue";
import ReferralLandingPage from "./pages/ReferralLandingPage";
import EcuadorLandingPage from "./pages/EcuadorLandingPage";
import PeruLandingPage from "./pages/PeruLandingPage";
import VIPPartnerDashboard from "./pages/VIPPartnerDashboard";
import AdminMigrateVendors from "./pages/admin/AdminMigrateVendors";
import AdminCleanDuplicates from "./pages/admin/AdminCleanDuplicates";
import "@/App.css";

import { emergencyExit } from "./components/ImpersonationBanner";

// Detectar loops de sesión corrupta
const detectSessionLoop = () => {
  const loopKey = 'session_loop_count';
  const loopTimestampKey = 'session_loop_timestamp';
  const now = Date.now();
  const lastTimestamp = parseInt(sessionStorage.getItem(loopTimestampKey) || '0');
  
  // Si han pasado más de 5 segundos, resetear contador
  if (now - lastTimestamp > 5000) {
    sessionStorage.setItem(loopKey, '1');
    sessionStorage.setItem(loopTimestampKey, now.toString());
    return false;
  }
  
  // Incrementar contador
  const count = parseInt(sessionStorage.getItem(loopKey) || '0') + 1;
  sessionStorage.setItem(loopKey, count.toString());
  sessionStorage.setItem(loopTimestampKey, now.toString());
  
  // Si hay más de 5 recargas en 5 segundos, es un loop
  if (count > 5) {
    console.error('Session loop detected! Clearing session...');
    sessionStorage.removeItem(loopKey);
    sessionStorage.removeItem(loopTimestampKey);
    return true;
  }
  
  return false;
};

// Ejecutar detección al cargar
if (detectSessionLoop()) {
  emergencyExit();
}

const ProtectedRoute = ({ children, adminOnly = false, staffOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // adminOnly - Solo admin puede acceder
  if (adminOnly && user.role !== "admin") {
    return <Navigate to={getDefaultRoute(user)} replace />;
  }
  
  // staffOnly - Solo admin (vendedor eliminado como rol)
  if (staffOnly && user.role !== "admin") {
    return <Navigate to={getDefaultRoute(user)} replace />;
  }
  
  return children;
};

// Helper to redirect based on user role
const getDefaultRoute = (user) => {
  if (!user) return "/";
  
  // VIP Partners go directly to their referral dashboard
  if (user.is_vip_partner && user.role === "client") {
    return "/mis-referidos";
  }
  
  switch (user.role) {
    case "admin":
      return "/admin";
    default:
      return "/dashboard";
  }
};

function AppRoutes() {
  const { user, fetchUser } = useAuth();
  const defaultRoute = getDefaultRoute(user);
  
  return (
    <>
      {/* Impersonation Banner - Shows when admin is viewing as another user */}
      {user?.is_impersonated && (
        <ImpersonationBanner user={user} onStopImpersonation={() => fetchUser()} />
      )}
      
      {/* Add top padding when impersonation banner is visible */}
      <div className={user?.is_impersonated ? "pt-12" : ""}>
        <Routes>
      <Route path="/" element={user ? <Navigate to={defaultRoute} replace /> : <LandingPage />} />
      <Route path="/preciosagentes" element={<AgentPricingPage />} />
      <Route path="/ecuador" element={<EcuadorLandingPage />} />
      <Route path="/peru" element={<PeruLandingPage />} />
      <Route path="/agentes/asesoramiento" element={<AgentReferralForm />} />
      <Route path="/terminos" element={<TermsAndConditions />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      {/* Referral landing page for VIP Partners */}
      <Route path="/r/:code" element={<ReferralLandingPage />} />
      <Route path="/login" element={user ? <Navigate to={defaultRoute} replace /> : <LoginPage />} />
      {/* Registration disabled - clients are created by admin only */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={user ? <Navigate to={defaultRoute} replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/mis-referidos" element={<ProtectedRoute><VIPPartnerDashboard /></ProtectedRoute>} />
      <Route path="/step/questionnaire" element={<ProtectedRoute><QuestionnaireStep /></ProtectedRoute>} />
      <Route path="/step/documents" element={<ProtectedRoute><DocumentsStep /></ProtectedRoute>} />
      <Route path="/step/ds160" element={<ProtectedRoute><DS160Step /></ProtectedRoute>} />
      <Route path="/step/optimization" element={<ProtectedRoute><OptimizationStep /></ProtectedRoute>} />
      <Route path="/step/result" element={<ProtectedRoute><ResultStep /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminCommandCenter /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/cases" element={<ProtectedRoute adminOnly><AdminCases /></ProtectedRoute>} />
      <Route path="/admin/cases/:caseId" element={<ProtectedRoute adminOnly><AdminCaseDetail /></ProtectedRoute>} />
      <Route path="/admin/case-management" element={<ProtectedRoute adminOnly><AdminCaseManagement /></ProtectedRoute>} />
      <Route path="/admin/chat" element={<ProtectedRoute adminOnly><AdminChat /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/metrics" element={<ProtectedRoute adminOnly><AdminRealTimeMetrics /></ProtectedRoute>} />
      <Route path="/admin/excluded-ips" element={<ProtectedRoute adminOnly><AdminExcludedIPs /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/staff" element={<ProtectedRoute adminOnly><AdminStaff /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute adminOnly><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/job-applications" element={<ProtectedRoute adminOnly><AdminJobApplications /></ProtectedRoute>} />
      <Route path="/admin/clients/new" element={<ProtectedRoute adminOnly><AdminCreateClient /></ProtectedRoute>} />
      <Route path="/admin/clients/search" element={<ProtectedRoute adminOnly><AdminClientSearch /></ProtectedRoute>} />
      <Route path="/admin/approvals" element={<ProtectedRoute adminOnly><AdminApprovals /></ProtectedRoute>} />
      {/* REMOVED: /admin/trial-emails - Trial system eliminated */}
      <Route path="/admin/documents" element={<ProtectedRoute adminOnly><AdminDocuments /></ProtectedRoute>} />
      <Route path="/admin/sla" element={<ProtectedRoute adminOnly><SLADashboard /></ProtectedRoute>} />
      <Route path="/admin/progress" element={<ProtectedRoute adminOnly><AdminStepsDashboard /></ProtectedRoute>} />
      <Route path="/admin/marketing" element={<ProtectedRoute adminOnly><AdminMarketingPanel /></ProtectedRoute>} />
      <Route path="/admin/commissions" element={<ProtectedRoute adminOnly><AdminCommissions /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute adminOnly><AdminProducts /></ProtectedRoute>} />
      <Route path="/admin/reminders" element={<ProtectedRoute adminOnly><AdminReminders /></ProtectedRoute>} />
      <Route path="/admin/live" element={<ProtectedRoute adminOnly><AdminLiveVisitors /></ProtectedRoute>} />
      <Route path="/admin/pagos-metricas" element={<ProtectedRoute adminOnly><AdminPaymentMetrics /></ProtectedRoute>} />
      <Route path="/admin/pagos-pendientes" element={<ProtectedRoute adminOnly><AdminPaymentsPending /></ProtectedRoute>} />
      <Route path="/admin/referidos" element={<ProtectedRoute adminOnly><AdminReferralCommissions /></ProtectedRoute>} />
      <Route path="/admin/migrar-vendedores" element={<ProtectedRoute adminOnly><AdminMigrateVendors /></ProtectedRoute>} />
      <Route path="/admin/limpiar-duplicados" element={<ProtectedRoute adminOnly><AdminCleanDuplicates /></ProtectedRoute>} />
      <Route path="/admin/cases/:caseId/ds160-review" element={<ProtectedRoute adminOnly><DS160ReviewPage /></ProtectedRoute>} />
      <Route path="/admin/standard-queue" element={<ProtectedRoute adminOnly><AdminStandardQueue /></ProtectedRoute>} />
      <Route path="/admin/ds160-pendientes" element={<ProtectedRoute adminOnly><AdminDS160Pendientes /></ProtectedRoute>} />
      <Route path="/admin/soporte" element={<ProtectedRoute adminOnly><AdminSupportChat /></ProtectedRoute>} />
      
      {/* Premium Features */}
      <Route path="/interview-simulator" element={<ProtectedRoute><InterviewSimulator /></ProtectedRoute>} />
      <Route path="/interview-questions" element={<ProtectedRoute><InterviewQuestions /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/soporte" element={<ProtectedRoute><SupportChatPage /></ProtectedRoute>} />
      <Route path="/simulacion-ia" element={<ProtectedRoute><AISimulationPage /></ProtectedRoute>} />
      <Route path="/autorevision" element={<ProtectedRoute><AutoRevisionPage /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><UpgradePlanPage /></ProtectedRoute>} />
      <Route path="/ds160-assistant" element={<ProtectedRoute><DS160Sections /></ProtectedRoute>} />
      <Route path="/ds160-mockup" element={<ProtectedRoute><DS160MockupDemo /></ProtectedRoute>} />
      <Route path="/ds160-old" element={<ProtectedRoute><DS160Assistant /></ProtectedRoute>} />
      
      {/* REMOVED: Role-specific Vendedor Dashboards - System simplified */}
      {/* All /vendedor routes now redirect to home */}
      <Route path="/vendedor" element={<Navigate to="/" replace />} />
      <Route path="/vendedor/*" element={<Navigate to="/" replace />} />
      
      {/* Rutas de Oficial - TODAS REDIRIGEN A ADMIN (rol oficial eliminado) */}
      <Route path="/oficial" element={<Navigate to="/admin" replace />} />
      <Route path="/oficial/*" element={<Navigate to="/admin" replace />} />
      <Route path="/oficial/cases/:caseId/ds160-review" element={<ProtectedRoute adminOnly><DS160ReviewPage /></ProtectedRoute>} />
      
      {/* Simulaciones movidas a Admin */}
      <Route path="/admin/simulaciones" element={<ProtectedRoute adminOnly><SimulacionesPanel /></ProtectedRoute>} />
      
      {/* Document Management */}
      <Route path="/documents" element={<ProtectedRoute><DocumentHistory /></ProtectedRoute>} />
      
      {/* Payment Pages */}
      <Route path="/pagar/:linkId" element={<PaymentLinkPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/schedule-simulation" element={<ProtectedRoute><ScheduleSimulation /></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Simplified - Removed LiveHelpWidget (cobrowsing eliminated) */}
      </div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteVisitorTracker />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: { fontFamily: 'Public Sans, sans-serif' }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
